// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Dobrev IT Ltd

import { useEffect, useRef, useState } from "react";
import { Dropdown, Empty, Tooltip, type MenuProps } from "antd";
import { MoreOutlined, SmileOutlined } from "@ant-design/icons";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import type { RoomEvent } from "../types";
import type { DrawOp } from "../whiteboard/types";
import { PollCard, type PollResult } from "./PollCard";
import { firstPreview, linkPreviewsEnabled } from "../linkPreview";

// LinkPreview renders an inline preview for the first previewable URL in a message — only
// the CORS-free cases (direct media, YouTube thumbnail), and only when the user has opted
// in (off by default, since loading remote media reveals their IP to that host).
function LinkPreview({ text }: { readonly text: string }) {
  if (!linkPreviewsEnabled()) return null;
  const p = firstPreview(text);
  if (!p) return null;
  if (p.kind === "youtube")
    return (
      <a className="link-preview link-preview-yt" href={p.url} target="_blank" rel="noopener noreferrer" title="Watch on YouTube">
        <img className="link-preview-img" src={p.thumb} alt="" loading="lazy" />
        <span className="link-preview-play">▶</span>
      </a>
    );
  if (p.kind === "video") return <video className="link-preview-media" src={p.url} controls preload="none" />;
  if (p.kind === "audio")
    return (
      <audio className="link-preview-audio" src={p.url} controls preload="none">
        <track kind="captions" />
      </audio>
    );
  return (
    <a className="link-preview" href={p.url} target="_blank" rel="noopener noreferrer">
      <img className="link-preview-img" src={p.url} alt="" loading="lazy" />
    </a>
  );
}

// MessageBody renders a message as constrained, XSS-safe Markdown (GFM): bold/italic/
// strikethrough, inline code, fenced code blocks, links, lists, quotes. react-markdown
// (v9) renders NO raw HTML by default and sanitises link URLs, so it's safe on untrusted
// text; we additionally drop images (we have first-class media) and force links to open
// safely. Paragraphs are kept tight so a one-line message still reads inline.
const mdComponents = {
  a: (props: React.AnchorHTMLAttributes<HTMLAnchorElement>) => <a {...props} target="_blank" rel="noopener noreferrer nofollow" />,
  p: (props: React.HTMLAttributes<HTMLParagraphElement>) => <p className="md-p" {...props} />,
};
export function MessageBody({ text }: { readonly text: string }) {
  return (
    // Drop images (we have first-class media) and headings (a chat line like "# 1" shouldn't
    // become a giant H1) — unwrapDisallowed keeps their inner text inline.
    <Markdown remarkPlugins={[remarkGfm]} disallowedElements={["img", "h1", "h2", "h3", "h4", "h5", "h6"]} unwrapDisallowed components={mdComponents}>
      {text}
    </Markdown>
  );
}

const QUICK_EMOJI = ["👍", "❤️", "😂", "🎉", "🙏", "👀"];

// One emoji's aggregation on a target: how many, did I, and who.
export interface ReactionAgg {
  count: number;
  mine: boolean;
  users: string[]; // reactor user IDs, for the "who reacted" tooltip
}

// ReactControl wires emoji reactions into the timeline: per-target aggregation
// and a toggle that adds or removes the viewer's reaction.
export interface ReactControl {
  // targetEventID -> emoji -> aggregation
  reactions: Record<string, Record<string, ReactionAgg>>;
  onToggle: (targetID: string, emoji: string) => void;
  nameOf: (userID: string) => string;
}

// reactionRow renders the reaction chips for a message plus a quick-react picker.
// Each chip tooltips the people who reacted with that emoji.
export function reactionRow(eventID: string, react: ReactControl) {
  const agg = react.reactions[eventID];
  const picker = (
    <Dropdown
      trigger={["click"]}
      menu={{ items: QUICK_EMOJI.map((e) => ({ key: e, label: <span className="emoji-opt">{e}</span>, onClick: () => react.onToggle(eventID, e) })) }}
    >
      <button type="button" className="react-add" title="React">
        <SmileOutlined />
      </button>
    </Dropdown>
  );
  return (
    <span className="reactions">
      {agg &&
        Object.entries(agg).map(([emoji, r]) => (
          <Tooltip key={emoji} title={r.users.map(react.nameOf).join(", ")}>
            <button type="button" className={`react-chip ${r.mine ? "mine" : ""}`} onClick={() => react.onToggle(eventID, emoji)}>
              {emoji} {r.count}
            </button>
          </Tooltip>
        ))}
      {picker}
    </span>
  );
}

function nick(userID: string, names?: Record<string, string>): string {
  return names?.[userID] || userID.replace(/^@/, "").split(":")[0];
}

function ts(ms: number): string {
  const d = ms ? new Date(ms) : new Date();
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

// messageLine renders a chat message (plain or decrypted), honouring /me emotes,
// with an optional reply quote, an "(edited)" marker, and trailing actions.
function messageLine(
  content: Record<string, unknown>,
  who: string,
  time: string,
  locked: boolean,
  opts?: { action?: React.ReactNode; quote?: React.ReactNode; edited?: boolean },
) {
  const body = String((content.body as string) ?? "");
  const emote = content.msgtype === "m.emote";
  const fwd = content["m.forwarded"] as { original_sender?: string } | undefined;
  // A message posted by an incoming webhook (server-originated, so PLAINTEXT) — show the
  // webhook's name + a "bot · not encrypted" badge instead of the @webhook nick.
  const bot = content["chat.zalmoxis.bot"] as { name?: string } | undefined;
  return (
    <div className="line line-msg">
      {fwd?.original_sender && (
        <span className="forwarded-from" title={`Forwarded from ${fwd.original_sender}`}>
          ⤳ Forwarded from {fwd.original_sender.replace(/^@/, "").split(":")[0]}
        </span>
      )}
      {opts?.quote}
      <span className="time">{time}</span>
      {locked && (
        <span className="lock" title="end-to-end encrypted">
          🔓
        </span>
      )}
      {emote ? (
        <span className="emote">
          * {who} {body}
        </span>
      ) : (
        <>
          {bot ? (
            <span className="nick bot-nick">
              &lt;{bot.name || who}&gt;
              <span className="bot-badge" title="Posted by a webhook — server-side, so not end-to-end encrypted">bot · unencrypted</span>
            </span>
          ) : (
            <span className="nick">&lt;{who}&gt;</span>
          )}
          <div className="msg md-body">
            <MessageBody text={body} />
            {opts?.edited && <span className="edited"> (edited)</span>}
            <LinkPreview text={body} />
          </div>
        </>
      )}
      {opts?.action}
    </div>
  );
}

function contentOf(ev: RoomEvent): Record<string, unknown> {
  return ev.decryptedContent ?? ev.content;
}

function relatesTo(ev: RoomEvent): { rel_type?: string; event_id?: string; ["m.in_reply_to"]?: { event_id?: string } } | undefined {
  return contentOf(ev)["m.relates_to"] as { rel_type?: string; event_id?: string } | undefined;
}

// isThreadReply reports whether an event is a threaded reply (lives in the thread,
// filtered from the main timeline).
function isThreadReply(ev: RoomEvent): boolean {
  return relatesTo(ev)?.rel_type === "m.thread";
}

// Poll events are plaintext (m.poll.start/response) or E2E (m.room.encrypted with
// an inner `type` in the decrypted content).
function pollStartContent(ev: RoomEvent): { question?: string; answers?: string[]; expires_ts?: number } | null {
  if (ev.type === "m.poll.start") return ev.content;
  if (ev.type === "m.room.encrypted" && (ev.decryptedContent as { type?: string })?.type === "m.poll.start") return ev.decryptedContent ?? null;
  return null;
}
function isPollResponse(ev: RoomEvent): boolean {
  if (ev.type === "m.poll.response") return true;
  return ev.type === "m.room.encrypted" && (ev.decryptedContent as { type?: string })?.type === "m.poll.response";
}

// A saved whiteboard snapshot (WB-013/014): plaintext m.whiteboard.snapshot, or an E2E
// m.room.encrypted whose decrypted content carries that type. Returns the (decrypted)
// content or null (e.g. not yet / can't decrypt — then it falls back to a normal line).
function snapshotContent(ev: RoomEvent): Record<string, unknown> | null {
  if (ev.type === "m.whiteboard.snapshot") return ev.content;
  if (ev.type === "m.room.encrypted" && (ev.decryptedContent as { type?: string })?.type === "m.whiteboard.snapshot") return ev.decryptedContent ?? null;
  return null;
}

function systemLine(time: string, text: string) {
  return (
    <div className="line line-system">
      <span className="time">{time}</span>
      <span className="sys">* {text}</span>
    </div>
  );
}

// fmtCallDuration renders a call length as m:ss (or h:mm:ss for long calls).
function fmtCallDuration(ms: number): string {
  const total = Math.round(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const mm = h > 0 ? String(m).padStart(2, "0") : String(m);
  return `${h > 0 ? `${h}:` : ""}${mm}:${String(s).padStart(2, "0")}`;
}

// callLine renders a Messenger-style call record. A 1:1 record (answered/missed, with
// duration) is posted by the caller, so direction is per-viewer (mine = outgoing); a group
// record ("X started a group call") is posted by whoever started it.
function callLine(content: Record<string, unknown>, outgoing: boolean, time: string, who: string) {
  const video = !!content.video;
  const icon = video ? "📹" : "📞";
  if (content.outcome === "group_started") {
    return (
      <div className="line line-call">
        <span className="time">{time}</span>
        <span className="call-record">
          {icon} {who} started a group call
        </span>
      </div>
    );
  }
  const missed = content.outcome === "missed";
  const durMs = Number(content.duration_ms ?? 0);
  let label: string;
  if (missed) label = outgoing ? "No answer" : "Missed call";
  else label = outgoing ? "Outgoing call" : "Incoming call";
  const dur = !missed && durMs > 0 ? ` · ${fmtCallDuration(durMs)}` : "";
  return (
    <div className={`line line-call${missed ? " line-call-missed" : ""}`}>
      <span className="time">{time}</span>
      <span className="call-record">
        {icon} {label}
        {dur}
      </span>
    </div>
  );
}

// memberText turns a membership change into an IRC-style action naming the target.
function memberText(ev: RoomEvent, who: string, names?: Record<string, string>): string {
  const membership = String((ev.content as { membership?: string }).membership ?? "updated");
  const target = ev.state_key ? nick(ev.state_key, names) : who;
  switch (membership) {
    case "join":
      return `${target} joined`;
    case "leave":
      return `${target} left`;
    case "invite":
      return `${who} invited ${target}`;
    case "knock":
      return `${target} knocked`;
    case "kick":
      return `${who} kicked ${target}`;
    case "ban":
      return `${who} banned ${target}`;
    default:
      return `${target} ${membership}`;
  }
}

// Line produces an IRC-style line for one event.
function Line({ ev, me, names, pin, react, edits, getEvent, msg, poll, receipts, thread, media, wb }: LineProps) {
  const who = nick(ev.sender, names);
  const time = ts(ev.origin_server_ts);

  // Call records render as their own compact line (answered/missed + duration), not a bubble.
  const c0 = contentOf(ev);
  if (ev.type === "m.room.message" && c0.msgtype === "m.call") {
    return callLine(c0, ev.sender === me, time, who);
  }

  // A saved whiteboard renders as a clickable card (WB-014), not a raw/garbled message.
  const snap = snapshotContent(ev);
  if (snap) {
    const ops = (snap.ops as DrawOp[]) ?? [];
    const count = Number(snap.count ?? ops.length);
    const title = String(snap.title ?? "Whiteboard");
    return (
      <div className="line line-wb">
        <span className="time">{time}</span>
        <button className="wb-card" onClick={() => wb?.onOpen(ops, title)} title="Open this saved whiteboard">
          <span className="wb-card-icon">🖊</span>
          <span className="wb-card-body">
            <span className="wb-card-title">{title}</span>
            <span className="wb-card-meta">Whiteboard · {count} item{count === 1 ? "" : "s"} · {who}</span>
          </span>
        </button>
      </div>
    );
  }

  if (ev.redacted_by) {
    const wasPoll = ev.type === "m.poll.start" || pollStartContent(ev) !== null;
    return systemLine(time, wasPoll ? "a poll was deleted" : `message from ${who} was redacted`);
  }

  // Polls render as their own inline card (plaintext or decrypted E2E).
  const pollC = pollStartContent(ev);
  if (pollC && ev.event_id) {
    const c = pollC;
    const result = poll?.polls[ev.event_id] ?? { counts: [], mine: null, total: 0 };
    return (
      <PollCard
        question={String(c.question ?? "")}
        answers={c.answers ?? []}
        result={result}
        expiresTs={c.expires_ts}
        canDelete={poll?.canDelete(ev) ?? false}
        onVote={(i) => poll?.onVote(ev.event_id, i)}
        onDelete={() => poll?.onDelete(ev.event_id)}
      />
    );
  }

  // Apply the latest edit (if any). The current body is what an edit should
  // prefill — not the original — so successive edits build on each other.
  const edited = Boolean(ev.event_id && edits?.[ev.event_id]);
  const effective = (ev.event_id && edits?.[ev.event_id]) || undefined;
  const currentBody = String(((effective ?? contentOf(ev)).body as string) ?? "");

  // Hover actions: reply (anyone), edit (own messages), pin (operators).
  const actions = (
    <span className="msg-actions">
      {msg && ev.event_id && (
        <button type="button" className="msg-action" title="Reply" onClick={() => msg.onReply(ev)}>
          ↩
        </button>
      )}
      {msg?.canEdit(ev) && ev.event_id && (
        <button type="button" className="msg-action" title="Edit" onClick={() => msg.onEdit(ev, currentBody)}>
          ✎
        </button>
      )}
      {msg && ev.event_id && (
        <button type="button" className="msg-action" title="Reply in thread" onClick={() => msg.onThread(ev)}>
          🧵
        </button>
      )}
      {msg && ev.event_id && (
        <button type="button" className="msg-action" title="Forward" onClick={() => msg.onForward(ev)}>
          ⤳
        </button>
      )}
      {msg && ev.event_id && (
        <button type="button" className="msg-action" title="Report" onClick={() => msg.onReport(ev)}>
          ⚑
        </button>
      )}
      {pin?.canPin && ev.event_id && (
        <button
          type="button"
          className={`pin-toggle ${pin.pinnedIds.has(ev.event_id) ? "pinned" : ""}`}
          title={pin.pinnedIds.has(ev.event_id) ? "Unpin" : "Pin"}
          onClick={() => pin.onToggle(ev.event_id, pin.pinnedIds.has(ev.event_id))}
        >
          📌
        </button>
      )}
    </span>
  );
  // Touch action menu: the same actions as the hover bar, reachable by tap. CSS
  // shows this only on coarse-pointer devices (and hides the hover bar there).
  const touchItems: MenuProps["items"] = [];
  if (msg && ev.event_id) {
    touchItems.push({ key: "reply", label: "Reply", onClick: () => msg.onReply(ev) });
    touchItems.push({ key: "thread", label: "Reply in thread", onClick: () => msg.onThread(ev) });
    touchItems.push({ key: "forward", label: "Forward", onClick: () => msg.onForward(ev) });
    touchItems.push({ key: "report", label: "Report", onClick: () => msg.onReport(ev) });
  }
  if (react && ev.event_id) {
    touchItems.push({
      key: "react",
      label: "React",
      children: QUICK_EMOJI.map((e) => ({ key: `r-${e}`, label: <span className="emoji-opt">{e}</span>, onClick: () => react.onToggle(ev.event_id, e) })),
    });
  }
  if (msg?.canEdit(ev) && ev.event_id) {
    touchItems.push({ key: "edit", label: "Edit", onClick: () => msg.onEdit(ev, currentBody) });
  }
  if (pin?.canPin && ev.event_id) {
    const pinned = pin.pinnedIds.has(ev.event_id);
    touchItems.push({ key: "pin", label: pinned ? "Unpin" : "Pin", onClick: () => pin.onToggle(ev.event_id, pinned) });
  }
  const touchMenu =
    touchItems.length > 0 ? (
      <Dropdown trigger={["click"]} menu={{ items: touchItems }} placement="bottomRight">
        <button type="button" className="msg-action-menu" title="Actions" aria-label="Message actions">
          <MoreOutlined />
        </button>
      </Dropdown>
    ) : null;

  const readers = (ev.event_id && receipts?.readBy[ev.event_id]) || [];
  const threadCount = (ev.event_id && thread?.counts[ev.event_id]) || 0;
  const threadNew = (ev.event_id && thread?.unread?.[ev.event_id]) || 0;
  const action = (
    <>
      {actions}
      {touchMenu}
      {threadCount > 0 && thread && (
        <button type="button" className={`thread-chip ${threadNew > 0 ? "has-unread" : ""}`} onClick={() => thread.onOpen(ev)}>
          🧵 {threadCount} {threadCount === 1 ? "reply" : "replies"}
          {threadNew > 0 && <span className="thread-new">{threadNew} new</span>}
        </button>
      )}
      {react && ev.event_id && reactionRow(ev.event_id, react)}
      {readers.length > 0 && receipts && (
        <span className="read-by" title={`Read by ${readers.map(receipts.nameOf).join(", ")}`}>
          {readers.slice(0, 3).map((uid) => (
            <span key={uid} className="reader-chip">
              {receipts.nameOf(uid).charAt(0).toUpperCase()}
            </span>
          ))}
        </span>
      )}
    </>
  );

  // Reply quote: render a small preview of the parent message.
  const parentId = relatesTo(ev)?.["m.in_reply_to"]?.event_id;
  const parent = parentId && getEvent ? getEvent(parentId) : undefined;
  const quote = parent ? (
    <span className="reply-quote">
      ↩ {nick(parent.sender, names)}: {String((contentOf(parent).body as string) ?? "")}
    </span>
  ) : undefined;

  const opts = { action, quote, edited };

  // Media messages render the (decrypted) attachment instead of a text body.
  const eff = (effective ?? contentOf(ev)) as Record<string, unknown>;
  if (
    media &&
    isMediaContent(eff) &&
    (ev.type === "m.room.message" || (ev.type === "m.room.encrypted" && ev.decryptedContent))
  ) {
    return mediaLine(eff, who, time, media, action);
  }

  switch (ev.type) {
    case "m.room.message":
      return messageLine(effective ?? ev.content, who, time, false, opts);
    case "m.room.encrypted":
      return ev.decryptedContent ? (
        messageLine(effective ?? ev.decryptedContent, who, time, true, opts)
      ) : (
        <div className="line">
          <span className="time">{time}</span>
          <span className="nick">&lt;{who}&gt;</span>
          <span className="msg encrypted">🔒 encrypted message</span>
        </div>
      );
    case "m.room.member":
      return systemLine(time, memberText(ev, who, names));
    case "m.room.topic":
      return systemLine(time, `${who} set the topic: ${String((ev.content as { topic?: string }).topic ?? "")}`);
    case "m.room.name":
      return systemLine(time, `${who} named the channel ${String((ev.content as { name?: string }).name ?? "")}`);
    case "m.room.join_rules":
      return systemLine(time, `${who} set join rule: ${String((ev.content as { join_rule?: string }).join_rule ?? "")}`);
    default:
      return systemLine(time, `${who} sent ${ev.type}`);
  }
}

// PinControl wires the operator pin/unpin toggle into the timeline.
export interface PinControl {
  canPin: boolean;
  pinnedIds: Set<string>;
  onToggle: (eventID: string, isPinned: boolean) => void;
}

// ThreadControl wires the thread indicator + open action into message lines.
export interface ThreadControl {
  counts: Record<string, number>; // root event ID -> reply count
  unread?: Record<string, number>; // root event ID -> unread reply count
  onOpen: (ev: RoomEvent) => void;
}

// MediaControl resolves a media message's content to a (decrypted) object URL.
export interface MediaControl {
  resolve: (content: Record<string, unknown>) => Promise<string>;
}

// VoiceMessage renders a compact audio-clip player (play/pause · seekable progress ·
// elapsed/total) for an m.audio message — WhatsApp/Telegram-style voice notes.
function VoiceMessage({ src, durationMs }: { readonly src: string; readonly durationMs?: number }) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [cur, setCur] = useState(0);
  const [dur, setDur] = useState(durationMs ? durationMs / 1000 : 0);
  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) void a.play();
    else a.pause();
  };
  const seek = (e: React.MouseEvent<HTMLDivElement>) => {
    const a = audioRef.current;
    if (!a || !dur) return;
    const r = e.currentTarget.getBoundingClientRect();
    a.currentTime = Math.max(0, Math.min(1, (e.clientX - r.left) / r.width)) * dur;
  };
  const frac = dur > 0 ? cur / dur : 0;
  const remaining = Math.max(0, dur - cur);
  return (
    <span className="voice-msg">
      <button type="button" className="voice-play" onClick={toggle} title={playing ? "Pause" : "Play"}>
        {playing ? "⏸" : "▶"}
      </button>
      <div className="voice-bar" onClick={seek}>
        <div className="voice-bar-fill" style={{ width: `${frac * 100}%` }} />
      </div>
      <span className="voice-time">{fmtCallDuration((playing || cur > 0 ? remaining : dur) * 1000)}</span>
      <audio
        ref={audioRef}
        src={src}
        preload="metadata"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => {
          setPlaying(false);
          setCur(0);
        }}
        onTimeUpdate={(e) => setCur(e.currentTarget.currentTime)}
        onLoadedMetadata={(e) => {
          const d = e.currentTarget.duration;
          if (Number.isFinite(d) && d > 0) setDur(d);
        }}
      >
        <track kind="captions" />
      </audio>
    </span>
  );
}

// MediaAttachment downloads + decrypts a media blob and renders it inline.
function MediaAttachment({ content, media }: { readonly content: Record<string, unknown>; readonly media: MediaControl }) {
  const [src, setSrc] = useState("");
  const [err, setErr] = useState(false);
  useEffect(() => {
    let url = "";
    let cancelled = false;
    media
      .resolve(content)
      .then((u) => {
        // Capture the URL unconditionally so cleanup can always revoke it — if the
        // attachment already unmounted, revoke immediately (else the blob leaks).
        url = u;
        if (cancelled) URL.revokeObjectURL(u);
        else setSrc(u);
      })
      .catch(() => {
        if (!cancelled) setErr(true);
      });
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content.url]);

  const name = String(content.body ?? "file");
  if (err) return <span className="media-err">⚠ couldn't load {name}</span>;
  if (!src) return <span className="media-loading">loading {name}…</span>;
  const type = String(content.msgtype);
  if (type === "m.image") return <img className="media-image" src={src} alt={name} />;
  if (type === "m.video") return <video className="media-video" src={src} controls />;
  if (type === "m.audio") return <VoiceMessage src={src} durationMs={Number(content.duration_ms) || undefined} />;
  return (
    <a className="media-file" href={src} download={name}>
      📎 {name}
    </a>
  );
}

// mediaLine renders a message whose content is an attachment.
function mediaLine(content: Record<string, unknown>, who: string, time: string, media: MediaControl, action?: React.ReactNode) {
  const fwd = content["m.forwarded"] as { original_sender?: string } | undefined;
  return (
    <div className="line line-msg">
      {fwd?.original_sender && (
        <span className="forwarded-from" title={`Forwarded from ${fwd.original_sender}`}>
          ⤳ Forwarded from {fwd.original_sender.replace(/^@/, "").split(":")[0]}
        </span>
      )}
      <span className="time">{time}</span>
      <span className="nick">&lt;{who}&gt;</span>
      <span className="msg media-msg">
        <MediaAttachment content={content} media={media} />
      </span>
      {action}
    </div>
  );
}

// isMediaContent reports whether a decrypted-or-plaintext content is an attachment.
function isMediaContent(c: Record<string, unknown>): boolean {
  return typeof c.url === "string" && !!c.file && ["m.image", "m.video", "m.audio", "m.file"].includes(String(c.msgtype));
}

// MsgControl wires reply/edit/thread actions into message lines.
export interface MsgControl {
  onReply: (ev: RoomEvent) => void;
  onEdit: (ev: RoomEvent, currentBody: string) => void;
  onThread: (ev: RoomEvent) => void;
  onReport: (ev: RoomEvent) => void;
  onForward: (ev: RoomEvent) => void;
  canEdit: (ev: RoomEvent) => boolean;
}

// PollControl wires poll tallies, voting, and deletion into the timeline.
export interface PollControl {
  polls: Record<string, PollResult>;
  onVote: (pollID: string, index: number) => void;
  onDelete: (pollID: string) => void;
  canDelete: (ev: RoomEvent) => boolean;
}

// ReceiptControl renders "read by" markers: eventID -> the readers parked there.
export interface ReceiptControl {
  readBy: Record<string, string[]>;
  nameOf: (userID: string) => string;
}

// WhiteboardControl opens a saved whiteboard snapshot from its timeline card (WB-014).
export interface WhiteboardControl {
  onOpen: (ops: DrawOp[], title: string) => void;
}

interface LineProps {
  ev: RoomEvent;
  me?: string; // the viewer's user id (for per-viewer call-record direction)
  names: Record<string, string>;
  pin?: PinControl;
  react?: ReactControl;
  edits?: Record<string, Record<string, unknown>>;
  getEvent?: (id: string) => RoomEvent | undefined;
  msg?: MsgControl;
  poll?: PollControl;
  receipts?: ReceiptControl;
  thread?: ThreadControl;
  media?: MediaControl;
  wb?: WhiteboardControl;
}

// isEdit reports whether an event is an m.replace edit (rendered into its target,
// not as its own line).
function isEdit(ev: RoomEvent): boolean {
  return relatesTo(ev)?.rel_type === "m.replace";
}

export function Timeline({
  events,
  me,
  names,
  pin,
  react,
  edits,
  getEvent,
  msg,
  poll,
  receipts,
  retentionMs,
  thread,
  media,
  wb,
}: {
  readonly events: RoomEvent[];
  readonly me?: string;
  readonly names: Record<string, string>;
  readonly pin?: PinControl;
  readonly react?: ReactControl;
  readonly edits?: Record<string, Record<string, unknown>>;
  readonly getEvent?: (id: string) => RoomEvent | undefined;
  readonly msg?: MsgControl;
  readonly poll?: PollControl;
  readonly receipts?: ReceiptControl;
  readonly retentionMs?: number; // disappearing-messages TTL (0/undefined = off)
  readonly thread?: ThreadControl;
  readonly media?: MediaControl;
  readonly wb?: WhiteboardControl;
}) {
  const endRef = useRef<HTMLDivElement>(null);
  // Re-render periodically so messages disappear live once they pass the TTL
  // (the server already drops them from reloads; this hides ones already on screen).
  const [, tick] = useState(0);
  useEffect(() => {
    if (!retentionMs) return;
    const t = setInterval(() => tick((n) => n + 1), 15000);
    return () => clearInterval(t);
  }, [retentionMs]);
  const cutoff = retentionMs ? Date.now() - retentionMs : 0;

  // Reactions, redactions, edits and poll votes aren't their own lines — they
  // annotate / modify / tally other events and are rendered accordingly. Expired
  // messages (disappearing TTL) are dropped too.
  const lines = events.filter((ev) => {
    // Thread replies live in the thread panel, not the main flow.
    if (ev.type === "m.reaction" || ev.type === "m.room.redaction" || isPollResponse(ev) || isEdit(ev) || isThreadReply(ev)) return false;
    // Polls + saved whiteboards aren't disappearing messages even though E2E ones are m.room.encrypted.
    const isMsg = (ev.type === "m.room.message" || ev.type === "m.room.encrypted") && !pollStartContent(ev) && !snapshotContent(ev);
    if (cutoff && isMsg && ev.origin_server_ts < cutoff) return false;
    return true;
  });
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [lines.length]);

  if (lines.length === 0) {
    return <div className="timeline empty-timeline">
      <Empty description="No messages yet" image={Empty.PRESENTED_IMAGE_SIMPLE} />
    </div>;
  }

  return (
    <div className="timeline">
      {lines.map((ev) => (
        <Line
          key={ev.event_id || `${ev.origin_server_ts}-${Math.random()}`}
          ev={ev}
          me={me}
          names={names}
          pin={pin}
          react={react}
          edits={edits}
          getEvent={getEvent}
          msg={msg}
          poll={poll}
          receipts={receipts}
          thread={thread}
          media={media}
          wb={wb}
        />
      ))}
      <div ref={endRef} />
    </div>
  );
}
